import type { ReactNode } from "react";
import { countDiffLines } from "@/lib/chat";
import { browserToolLabel, isBrowserTool } from "@/lib/browser-tool-labels";
import type { ToolUseRenderBlock } from "@/lib/chat-render";
import { editorCommandLabel, toolDisplayName } from "@/lib/tool-catalog";
import { DiffLineStats } from "@/components/diff/DiffLineStats";

/**
 * page_range 入力が、サーバが受け付ける形 (整数ちょうど 2 個・開始が終了以下) のときだけ
 * その範囲を返す。開始が終了より後の範囲はサーバが拒否するので表示しない。
 */
function pageRangeOf(input: unknown): [number, number] | null {
	if (!Array.isArray(input) || input.length !== 2) return null;
	const [start, end] = input as unknown[];
	if (!Number.isInteger(start) || !Number.isInteger(end)) return null;
	return (start as number) <= (end as number) ? [start as number, end as number] : null;
}

/** 編集ツールの差分の行数表示。差分が無いツールでは null。 */
export function editDiffStats(tool: ToolUseRenderBlock): ReactNode {
	if (
		isBrowserTool(tool) ||
		tool.name !== "str_replace_based_edit_tool" ||
		!["str_replace", "insert", "create"].includes(tool.input.command as string) ||
		!tool.diff
	) {
		return null;
	}
	const counts = countDiffLines(tool.diff.hunks);
	return <DiffLineStats added={counts.added} removed={counts.removed} />;
}

/** ツール1件を1行で言い表すラベル (差分の行数表示を除く)。チャットのツールカードとタスク詳細で共有する。 */
export function toolEntryText(tool: ToolUseRenderBlock): string {
	// toolset のメンバー名 (screenshot / key 等) は他ツールと衝突しうるので、名前より先に toolset で分岐する。
	if (isBrowserTool(tool)) return browserToolLabel(tool);
	if (tool.name === "bash") {
		const command = typeof tool.input.command === "string" ? tool.input.command.trim() : "";
		const firstLine = command.split("\n")[0].trim();
		return firstLine ? `Bash (${firstLine})` : "Bash";
	}
	if (tool.name === "str_replace_based_edit_tool") {
		const path = typeof tool.input.path === "string" ? tool.input.path : "";
		const action = editorCommandLabel(tool.input.command);
		if (action) return `${action} (${path})`;
	}
	if (tool.name === "glob" || tool.name === "grep") {
		const pattern = typeof tool.input.pattern === "string" ? tool.input.pattern : "";
		const path = typeof tool.input.path === "string" ? tool.input.path.trim() : "";
		const target = [pattern, path && `${path} 内`].filter(Boolean).join(" · ");
		return target ? `${toolDisplayName(tool.name)} (${target})` : toolDisplayName(tool.name);
	}
	if (tool.name === "view_image") {
		const path = typeof tool.input.path === "string" ? tool.input.path : "";
		return path ? `画像を表示 (${path})` : "画像を表示";
	}
	if (tool.name === "open_file") {
		const path = typeof tool.input.path === "string" ? tool.input.path : "";
		return path ? `ファイルを開く (${path})` : "ファイルを開く";
	}
	if (tool.name === "view_document") {
		const path = typeof tool.input.path === "string" ? tool.input.path : "";
		if (!path) return "文書を読み込み";
		// 大きな文書は複数回に分けて読むので、ページ範囲がないと同じ呼び出しが並んでいるように見える。
		// サーバが受け付ける形 (整数ちょうど 2 個) だけを表示する。それ以外を表示すると、
		// 実際には拒否された呼び出しを「その範囲を読んだ」と偽って見せてしまう。
		// なお、この範囲はサーバが実際に読んだページへクランプする前の「指定値」なので、
		// 「読んだ」ではなく「指定範囲」と明示する。
		const range = pageRangeOf(tool.input.page_range);
		return range
			? `文書を読み込み (${path} 指定範囲 ${range[0]}-${range[1]}ページ)`
			: `文書を読み込み (${path})`;
	}
	if (tool.name === "web_search") {
		const query = typeof tool.input.query === "string" ? tool.input.query.trim() : "";
		return query ? `Web検索 (${query})` : "Web検索";
	}
	if (tool.name === "web_fetch") {
		const url = typeof tool.input.url === "string" ? tool.input.url.trim() : "";
		return url ? `Webページを取得 (${url})` : "Webページを取得";
	}
	// 汎用カードへ回るのは run 行が作られなかった起動 (拒否・入力検証エラー) だけ。名前だけでは
	// どのタスクが落ちたのか分からないので、起動時の説明を添える。
	if (tool.name === "run_subagent") {
		const description =
			typeof tool.input.description === "string" ? tool.input.description.trim() : "";
		return description ? `サブエージェント (${description})` : toolDisplayName(tool.name);
	}
	return toolDisplayName(tool.name);
}

/** 展開して見せられる詳細 (入力・出力・画像・差分のいずれか) を持つか。 */
export function hasToolDetails(tool: ToolUseRenderBlock): boolean {
	return (
		Boolean(tool.output) ||
		Object.keys(tool.input).length > 0 ||
		tool.diff !== undefined ||
		Boolean(tool.images?.length)
	);
}

export function toolCopyText(tool: ToolUseRenderBlock): string {
	const sections = [
		`ツール: ${toolEntryText(tool)}`,
		`入力:\n${JSON.stringify(tool.input, null, 2)}`,
	];
	const imageCount = tool.images?.length ?? 0;
	const outputParts = [tool.output, imageCount > 0 ? `(画像 ${imageCount} 枚)` : ""].filter(
		Boolean,
	);
	if (outputParts.length > 0) sections.push(`出力:\n${outputParts.join("\n")}`);
	return sections.join("\n\n");
}
